import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Dialog, Transition } from '@headlessui/react';
import Select from 'react-select';
import { useAuth } from '../../../context/AuthContext';
import { userAPI, recruiterAPI } from '../../../utils/api';
import { ASSIGNABLE_ROLE_OPTIONS, findRoleOption, toRoleCode } from '../../../utils/roleUtils';
import AppIcon from '../../../components/AppIcon';

const EditUserModal = ({ isOpen, onClose, user, onUserUpdated, showNotification }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const [employers, setEmployers] = useState([]);
  const { user: currentUser } = useAuth();

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm();

  const role = watch('role');

  useEffect(() => {
    if (user) {
      reset({
        full_name: user.full_name || '',
        username: user.username || '',
        email: user.email || '',
        role: findRoleOption(user.role) || { value: user.role, label: user.role_name },
        status: { value: user.status?.toLowerCase(), label: user.status },
        recruiter_id: user.recruiter ? { value: user.recruiter.id, label: user.recruiter.agency_name } : null,
        employer_id: user.employer ? { value: user.employer.id, label: user.employer.company_name } : null,
      });
    }
  }, [user, reset]);

  useEffect(() => {
    const fetchRecruiters = async () => {
      try {
        const response = await recruiterAPI.getAllRecruiters();
        // Handle nested response structure
        const data = response?.data?.data || response?.data || response || [];
        setRecruiters(data.map(r => ({ value: r.id, label: r.name })));
      } catch (error) {
        console.error('Failed to fetch recruiters:', error);
      }
    };
    fetchRecruiters();
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // The list API returns the raw role ('0'/'1'/'2'), while the select uses
      // named values, so accept either form instead of defaulting a CLAB user
      // to recruiter on every save.
      const roleCode = toRoleCode(data.role?.value);

      const payload = {
        name: data.full_name,
        email: data.email,
        status: data.status.value,
        role: roleCode,
      };

      if (roleCode === '2' && data.recruiter_id?.value) {
        payload.recruiterId = data.recruiter_id.value;
      }

      if (data.password) {
        payload.password = data.password;
      }
      
      await userAPI.updateUser(user.id, payload);
      onUserUpdated(true, 'User updated successfully!');
      onClose();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      onUserUpdated(false, errorMessage);
      console.error('Failed to update user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) return null;

  const roleOptions = ASSIGNABLE_ROLE_OPTIONS;

  // The stored role is '0'/'1'/'2' while the select uses named values.
  const selectedRole = String(role?.value ?? '');
  const isRecruiterRole = selectedRole === 'recruiter' || selectedRole === '2';
  const isEmployerRole = selectedRole === 'employer' || selectedRole === '3';

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
    { value: 'suspended', label: 'Suspended' },
  ];

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        {/* ... The rest of the modal JSX is restored here ... */}
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Edit User
                      </Dialog.Title>
                      <div className="mt-2 space-y-4">
                        <div>
                          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">Full Name</label>
                          <input type="text" {...register('full_name', { required: 'Full name is required' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name.message}</p>}
                        </div>

                        <div>
                          <label htmlFor="username" className="block text-sm font-medium text-gray-700">Username</label>
                          <input type="text" {...register('username', { required: 'Username is required' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username.message}</p>}
                        </div>

                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                          <input type="email" {...register('email', { required: 'Email is required' })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                        </div>

                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">New Password (optional)</label>
                          <input type="password" {...register('password')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                        </div>

                        <div>
                            <label htmlFor="role" className="block text-sm font-medium text-gray-700">Role</label>
                            <Controller
                                name="role"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select {...field} options={roleOptions} className="mt-1 block w-full" />
                                )}
                            />
                        </div>

                        {isRecruiterRole && (
                            <div>
                                <label htmlFor="recruiter_id" className="block text-sm font-medium text-gray-700">Recruiter</label>
                                <Controller
                                    name="recruiter_id"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} options={recruiters} isClearable className="mt-1 block w-full" />
                                    )}
                                />
                            </div>
                        )}

                        {isEmployerRole && (
                            <div>
                                <label htmlFor="employer_id" className="block text-sm font-medium text-gray-700">Employer</label>
                                <Controller
                                    name="employer_id"
                                    control={control}
                                    render={({ field }) => (
                                        <Select {...field} options={employers} isClearable className="mt-1 block w-full" />
                                    )}
                                />
                            </div>
                        )}

                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700">Status</label>
                            <Controller
                                name="status"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Select {...field} options={statusOptions} className="mt-1 block w-full" />
                                )}
                            />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="submit"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Updating...' : 'Update User'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </Dialog.Panel>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default EditUserModal;